import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { 
    Box, Paper, Typography, Chip, Avatar, Button, Divider, 
    useTheme, alpha, Stack, Dialog, DialogContent, IconButton 
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faStore, faClock, faUserTie, faChair, faQrcode, faTimes, 
    faLocationArrow, faMapMarkerAlt 
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import QRCode from "react-qr-code";

export default function BookingCardFinal({ booking, onCancel }) {
    const theme = useTheme();
    const { t } = useTranslation();
    const history = useHistory(); 
    const [openQr, setOpenQr] = useState(false);
    
    const { uuid, service_name, calendar, time_range, merchant, resource, status, can_cancel } = booking;
    const resourceIcon = resource.type === 'employee' ? faUserTie : faChair;
    const mainColor = theme.palette.link?.main || '#1976d2'; 

    return (
        <>
            <Paper 
                elevation={0}
                sx={{ 
                    mb: 2, borderRadius: 4, overflow: 'hidden',
                    border: '1px solid', borderColor: 'divider',
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' }, // Stack en móvil, fila en PC
                    transition: 'all 0.2s', 
                    '&:hover': { 
                        borderColor: mainColor, 
                        boxShadow: `0 4px 15px ${alpha(mainColor, 0.1)}`,
                        transform: 'translateY(-2px)'
                    }
                }}
            >
                {/* 1. SECCIÓN FECHA (Badge Estirado) */}
                {/* Usamos un Box con margen para que parezca un "Bloque" dentro de la tarjeta */}
                <Box sx={{ 
                    // Ancho fijo en escritorio, alto automático
                    width: { xs: '100%', sm: 120 }, 
                    // Margen de 2 unidades (16px) para separarlo del borde real de la tarjeta
                    m: { xs: 0, sm: 2 },
                    mb: { xs: 0 }, // En móvil pegado arriba
                    borderRadius: { xs: 0, sm: 3 }, // Redondeado en PC, cuadrado en móvil
                    
                    bgcolor: alpha(mainColor, 0.08), 
                    color: mainColor,
                    
                    display: 'flex', flexDirection: { xs: 'row', sm: 'column' },
                    alignItems: 'center', justifyContent: 'center',
                    p: 2, gap: 0.5,
                    // Borde sutil solo para definir la forma
                    border: { sm: `1px solid ${alpha(mainColor, 0.1)}` },
                    borderBottom: { xs: `1px solid ${alpha(mainColor, 0.1)}`, sm: 'none' } // Línea divisoria en móvil
                }}>
                    <Typography variant="caption" fontWeight="bold" sx={{textTransform:'uppercase', letterSpacing:1}}>{calendar.month}</Typography>
                    <Typography variant="h3" fontWeight="900" sx={{ lineHeight: 1 }}>{calendar.day}</Typography>
                    <Typography variant="caption" fontWeight="bold" sx={{ opacity: 0.8 }}>{calendar.weekday}</Typography>
                </Box>

                {/* 2. CONTENIDO (Derecha) */}
                {/* Py: Padding vertical ajustado para centrar contenido */}
                <Box p={3} flexGrow={1} display="flex" flexDirection="column" justifyContent="center">
                    
                    {/* Header: Título y Estatus */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Box>
                            <Typography variant="h6" fontWeight="800" sx={{lineHeight:1.2, mb: 0.5}}>
                                {service_name}
                            </Typography>
                            
                            {/* Link Comercio + Cómo llegar */}
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1}>
                                <FontAwesomeIcon icon={faStore} size="xs" color={theme.palette.text.secondary}/>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ cursor:'pointer', fontWeight: 500, '&:hover':{color:mainColor, textDecoration:'underline'} }}
                                    onClick={() => history.push(`/merchants/${merchant.id}/profile`)}
                                >
                                    {merchant.name}
                                </Typography>
                                
                                {(merchant.lat && merchant.lng) && (
                                    <Chip 
                                        label="Cómo llegar" 
                                        size="small" 
                                        icon={<FontAwesomeIcon icon={faLocationArrow} size="xs"/>} 
                                        onClick={(e)=>{e.stopPropagation(); window.open(`http://maps.google.com/?q=${merchant.lat},${merchant.lng}`)}} 
                                        sx={{ 
                                            height: 20, fontSize: '0.65rem', cursor: 'pointer', fontWeight: 'bold',
                                            bgcolor: 'success.lighter', color: 'success.dark',
                                            '&:hover': { bgcolor: 'success.light', color: 'white' },
                                            // Icon adjustment
                                            '& .MuiChip-icon': { fontSize: 10, color: 'inherit' }
                                        }} 
                                    />
                                )}
                            </Stack>
                        </Box>
                        
                        <Chip label={status.label} size="small" sx={{ fontWeight:'bold', bgcolor: status.code==='confirmed'?'success.lighter':'grey.100', color: status.code==='confirmed'?'success.dark':'grey.700', borderRadius: 2 }} />
                    </Box>

                    {/* Divider Sutil */}
                    <Box sx={{ my: 2, height: 1, borderBottom: '1px dashed', borderColor: 'divider', width: '100%' }} />

                    {/* Detalles (LIMPIO / SIN FONDO GRIS) */}
                    <Box display="flex" flexWrap="wrap" gap={{ xs: 3, md: 5 }} alignItems="center" mb={3}>
                        
                        {/* HORA */}
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <FontAwesomeIcon icon={faClock} color={theme.palette.text.disabled} size="lg"/>
                            <Box>
                                <Typography variant="caption" display="block" color="text.disabled" fontWeight="bold">HORA</Typography>
                                <Typography variant="body2" fontWeight="700">{time_range}</Typography>
                            </Box>
                        </Box>

                        {/* RECURSO */}
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar src={resource.image} sx={{ width: 24, height: 24, bgcolor: 'transparent', color: theme.palette.text.disabled, border: '1px solid #eee' }}>
                                <FontAwesomeIcon icon={resourceIcon} size="xs"/>
                            </Avatar>
                            <Box>
                                <Typography variant="caption" display="block" color="text.disabled" fontWeight="bold">
                                    {resource.type === 'employee' ? 'ESPECIALISTA' : 'ZONA'}
                                </Typography>
                                <Typography variant="body2" fontWeight="700">{resource.name}</Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Footer: Botones */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems="center">
                        <Button 
                            variant="outlined" 
                            color="inherit" 
                            startIcon={<FontAwesomeIcon icon={faQrcode} />} 
                            onClick={() => setOpenQr(true)} 
                            sx={{ 
                                borderRadius: 50, px: 3, 
                                width: { xs: '100%', sm: 'auto' }, 
                                borderColor: 'divider',
                                color: 'text.primary',
                                '&:hover': { borderColor: 'text.primary', bgcolor: 'transparent' }
                            }}
                        >
                            Ticket QR
                        </Button>

                        {can_cancel && (
                            <Button 
                                variant="text" 
                                color="error" 
                                onClick={() => onCancel(booking)} 
                                sx={{ 
                                    borderRadius: 50, px: 2, fontWeight:'bold', 
                                    '&:hover': { bgcolor: 'error.lighter' } 
                                }}
                            >
                                {t('bookings.cancel_btn')}
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Paper>

            {/* Modal QR */}
            <Dialog open={openQr} onClose={() => setOpenQr(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{textAlign:'center', py:4}}>
                    <Box display="flex" justifyContent="flex-end">
                        <IconButton size="small" onClick={()=>setOpenQr(false)}><FontAwesomeIcon icon={faTimes}/></IconButton>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>{service_name}</Typography>
                    <Box sx={{my:2, p:2, border:'2px dashed #eee', display:'inline-block', borderRadius:2}}>
                        <QRCode value={uuid} size={150} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">Muestra este código al llegar</Typography>
                </DialogContent>
            </Dialog>
        </>
    );
}